/**
 * GPU compute pipeline for angle-weighted smooth normals.
 *
 * Replaces the CPU-side recomputeMorphedNormals pipeline. Three compute passes
 * derive correct normals from actual morphed+skinned vertex positions:
 *
 *   Pass 0 (per-vertex): Morph blend + sculpt + bone skinning → skinned positions.
 *                         Also clears normal accumulators.
 *   Pass 1 (per-triangle): Face normal + angle-weighted atomic accumulation.
 *                         Accumulates to canonical vertex indices so UV seam
 *                         splits receive normals from ALL adjacent triangles.
 *   Pass 2 (per-vertex): Read accumulated normal from canonical representative,
 *                         normalize, and write to output buffer.
 *
 * Result stays in a GPU storage buffer read directly by the vertex shader —
 * no CPU round-trip, no debounce delay.
 *
 * IMPORTANT: All buffers used by compute shaders MUST use
 * StorageBufferAttribute — regular BufferAttribute only gets VERTEX
 * usage, not STORAGE. Do NOT use StorageInstancedBufferAttribute — it
 * extends InstancedBufferAttribute which reads per-instance, not
 * per-vertex, causing flat shading.
 */

import {
  abs,
  atan,
  atomicAdd,
  atomicLoad,
  atomicStore,
  buffer,
  cross,
  dot,
  Fn,
  float,
  If,
  instanceIndex,
  int,
  Loop,
  mix,
  select,
  sqrt,
  storage,
  uint,
  uniform,
  vec3,
  vec4,
} from "three/tsl";
import type * as THREE from "three/webgpu";
import { StorageBufferAttribute } from "three/webgpu";
import { buildAdjacencyForSmooth } from "./deformationModes.ts";
import { shadingDiag } from "./shadingDiag.ts";

// TSL builder types are incomplete — the runtime API is richer than the TS
// declarations. We use `any` for storage/buffer nodes that chain .setName(),
// .toReadOnly(), .element(), etc.
/* eslint-disable @typescript-eslint/no-explicit-any */

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface GpuNormalCompute {
  /** Three compute nodes to dispatch each frame (in order). */
  computeNodes: any[];
  /** Storage buffer node for computed normals. */
  normalBuffer: any;
  /** Storage buffer node for skinned positions (for CPU readback to proxy mesh). */
  positionBuffer: any;
  /** The raw StorageBufferAttribute backing the position storage buffer. */
  positionSBA: InstanceType<typeof StorageBufferAttribute>;
  /** Float32Array backing morph influences — write current values here before dispatch. */
  influenceData: Float32Array;
  /** The raw StorageBufferAttribute backing morph influences (flag needsUpdate after sync). */
  influenceSBA: InstanceType<typeof StorageBufferAttribute> | null;
  /** The raw StorageBufferAttribute backing sculpt displacement storage. */
  sculptSBA: InstanceType<typeof StorageBufferAttribute>;
  /** The raw StorageBufferAttribute backing the normal output (for CPU readback / diagnostics). */
  normalSBA: InstanceType<typeof StorageBufferAttribute>;
  /** Corrective smooth passes (A=pos→scratch, B=scratch→pos). Null if not available. */
  smoothPassA: any;
  smoothPassB: any;
  /** TSL uniform nodes for deformation modes — update .value directly. */
  dqsBlendUniform: any;
  smoothFactorUniform: any;
  /** Dispose GPU resources. */
  dispose: () => void;
}

/**
 * Build the GPU compute pipeline for a skinned mesh.
 *
 * When `enableDeformModes` is true, the pipeline includes:
 *   - DQS blending (controlled by dqsBlendUniform.value, 0=LBS / 1=DQS)
 *   - Corrective smooth passes (dispatched by MannequinRenderer when enabled)
 * Update the returned uniform nodes (.value) each frame to control these.
 */
/**
 * Create a GPU compute pipeline for a SkinnedMesh.
 *
 * @param morphPositions - Explicit morph position arrays.  When provided,
 *   these are used instead of reading geo.morphAttributes.position.  This
 *   lets the caller keep morphAttributes off the geometry (preventing
 *   Three.js from compiling its own morph handling) while still feeding
 *   morph deltas to the compute shader.
 */
export function createGpuNormalCompute(
  mesh: THREE.SkinnedMesh,
  enableDeformModes?: boolean,
  morphPositions?: THREE.BufferAttribute[],
): GpuNormalCompute {
  const geo = mesh.geometry;
  const posAttr = geo.getAttribute("position") as THREE.BufferAttribute;
  const idxAttr = geo.index!;
  const vertCount = posAttr.count;
  const triCount = idxAttr.count / 3;

  // --- Storage buffers ---
  // All writable buffers use StorageBufferAttribute to get
  // GPUBufferUsage.STORAGE | VERTEX flags. Regular BufferAttribute only
  // gets VERTEX, so compute shaders silently fail to write.
  // IMPORTANT: Do NOT use StorageInstancedBufferAttribute — it extends
  // InstancedBufferAttribute which steps per-instance (not per-vertex),
  // causing all vertices to read element 0 → flat shading.

  // Skinned positions output (read-write by compute, read by tri normal pass)
  const skinnedPosData = new Float32Array(vertCount * 4);
  const positionSBA = new (StorageBufferAttribute as any)(skinnedPosData, 4);
  const positionBuffer = (storage as any)(
    positionSBA,
    "vec4",
    vertCount,
  ).setName("skinnedPositions");

  // Pre-skin morphed positions (base + morphs + sculpt, BEFORE bone skinning).
  // Only allocated in render mode (when morphPositions are passed explicitly),
  // where morphAttributes are stripped from the geometry and the vertex shader
  // Normal accumulators — single interleaved int32 buffer (X,Y,Z per vertex).
  // WebGPU atomics only work on scalar atomic<i32>, not vec4 components.
  // Merged into one buffer to stay within the 8 storage buffers per stage limit.
  const accumData = new Int32Array(vertCount * 3);
  const accumSBA = new (StorageBufferAttribute as any)(accumData, 1);
  const accumBuffer = (storage as any)(accumSBA, "int", vertCount * 3)
    .toAtomic()
    .setName("normalAccum");

  // Final normalized normals — initialized with base normals so the
  // mannequin renders correctly even before the first compute dispatch.
  // Uses vec3 (itemSize=3) so this SBA can directly replace the geometry's
  // "normal" attribute — making normalLocal automatically read GPU-computed
  // normals through the standard pipeline (normalLocal → normalViewGeometry
  // → normalView → matcapUV and all other consumers).
  const normalOutData = new Float32Array(vertCount * 3);
  const baseNrm = geo.getAttribute("normal") as
    | THREE.BufferAttribute
    | undefined;
  if (baseNrm) {
    const nrmArr = baseNrm.array as Float32Array;
    for (let i = 0; i < vertCount; i++) {
      normalOutData[i * 3] = nrmArr[i * 3];
      normalOutData[i * 3 + 1] = nrmArr[i * 3 + 1];
      normalOutData[i * 3 + 2] = nrmArr[i * 3 + 2];
    }
  }
  const normalOutSBA = new (StorageBufferAttribute as any)(normalOutData, 3);
  const normalBuffer = (storage as any)(
    normalOutSBA,
    "vec3",
    vertCount,
  ).setName("computedNormals");

  // Index buffer (read-only)
  const indices = new Uint32Array(idxAttr.array as ArrayLike<number>);
  const indexSBA = new (StorageBufferAttribute as any)(indices, 1);
  const indexBuffer = (storage as any)(indexSBA, "uint", indices.length)
    .toReadOnly()
    .setName("indexBuffer");

  // Base positions (read-only, vec4-padded from vec3 source)
  const basePosData = new Float32Array(vertCount * 4);
  const srcPos = posAttr.array as Float32Array;
  for (let i = 0; i < vertCount; i++) {
    basePosData[i * 4] = srcPos[i * 3];
    basePosData[i * 4 + 1] = srcPos[i * 3 + 1];
    basePosData[i * 4 + 2] = srcPos[i * 3 + 2];
    basePosData[i * 4 + 3] = 1;
  }
  const basePosSBA = new (StorageBufferAttribute as any)(basePosData, 4);
  const basePosBuffer = (storage as any)(basePosSBA, "vec4", vertCount)
    .toReadOnly()
    .setName("basePositions");

  // --- Morph targets ---
  // --- Morph targets ---
  // When morphPositions is provided explicitly, use those. Otherwise fall
  // back to the geometry's own morphAttributes.  Providing them explicitly
  // lets the caller keep morphAttributes off the geometry (preventing
  // Three.js from compiling morph handling into its vertex shader) while
  // still feeding morph deltas to the compute shader.
  const resolvedMorphs: THREE.BufferAttribute[] | undefined =
    morphPositions ??
    (geo.morphAttributes.position as THREE.BufferAttribute[] | undefined);
  const morphCount = resolvedMorphs?.length ?? 0;

  // Pack all morph deltas into one flat buffer: morphCount * vertCount vec4s
  let morphDeltaBuffer: any = null;
  let morphSBA: InstanceType<typeof StorageBufferAttribute> | null = null;
  if (morphCount > 0 && resolvedMorphs) {
    const morphBufferSizeMB = (morphCount * vertCount * 4 * 4) / (1024 * 1024);
    shadingDiag.log(
      "BUFFER",
      "morph-buffer-alloc",
      `Morph delta buffer: ${morphCount} morphs × ${vertCount} verts = ${morphBufferSizeMB.toFixed(1)} MB`,
    );
    if (morphBufferSizeMB > 30) {
      shadingDiag.warn(
        "BUFFER",
        "morph-buffer-large",
        `Large morph buffer (${morphBufferSizeMB.toFixed(1)} MB) — may cause GPU memory pressure`,
      );
    }
    const morphData = new Float32Array(morphCount * vertCount * 4);
    for (let mi = 0; mi < morphCount; mi++) {
      const deltas = resolvedMorphs[mi].array as Float32Array;
      const offset = mi * vertCount * 4;
      for (let vi = 0; vi < vertCount; vi++) {
        morphData[offset + vi * 4] = deltas[vi * 3];
        morphData[offset + vi * 4 + 1] = deltas[vi * 3 + 1];
        morphData[offset + vi * 4 + 2] = deltas[vi * 3 + 2];
      }
    }
    morphSBA = new (StorageBufferAttribute as any)(morphData, 4);
    morphDeltaBuffer = (storage as any)(
      morphSBA,
      "vec4",
      morphCount * vertCount,
    )
      .toReadOnly()
      .setName("morphDeltas");
  }

  // Morph influences — updated from JS each frame.
  // Uses storage buffer (not uniform) because WGSL requires uniform arrays
  // to have 16-byte stride, but influences are scalar floats (4-byte stride).
  const influenceData = new Float32Array(Math.max(morphCount, 1));
  let influenceBuffer: any = null;
  let influenceSBA: InstanceType<typeof StorageBufferAttribute> | null = null;
  if (morphCount > 0) {
    influenceSBA = new (StorageBufferAttribute as any)(influenceData, 1);
    influenceBuffer = (storage as any)(influenceSBA, "float", morphCount)
      .toReadOnly()
      .setName("morphInfluences");
  }

  // --- Skin data (read-only) ---
  const skinIndexAttr = geo.getAttribute("skinIndex") as THREE.BufferAttribute;
  const skinWeightAttr = geo.getAttribute(
    "skinWeight",
  ) as THREE.BufferAttribute;

  const skinIdxSBA = new (StorageBufferAttribute as any)(
    new Uint32Array(skinIndexAttr.array),
    4,
  );
  const skinIndexBuf = (storage as any)(skinIdxSBA, "uvec4", vertCount)
    .toReadOnly()
    .setName("skinIndex");

  const skinWgtSBA = new (StorageBufferAttribute as any)(
    new Float32Array(skinWeightAttr.array as Float32Array),
    4,
  );
  const skinWeightBuf = (storage as any)(skinWgtSBA, "vec4", vertCount)
    .toReadOnly()
    .setName("skinWeight");

  // Bone matrices
  const skeleton = mesh.skeleton;
  const boneMatBuf = (buffer as any)(
    skeleton.boneMatrices,
    "mat4",
    skeleton.bones.length,
  );
  const bindMatUniform = (uniform as any)(mesh.bindMatrix, "mat4");
  const bindMatInvUniform = (uniform as any)(mesh.bindMatrixInverse, "mat4");

  // --- Sculpt displacement (read-only by compute, updated from JS) ---
  const sculptData = new Float32Array(vertCount * 4);
  const sculptSBA = new (StorageBufferAttribute as any)(sculptData, 4);
  const sculptBuffer = (storage as any)(sculptSBA, "vec4", vertCount)
    .toReadOnly()
    .setName("sculptDisplacement");

  // --- Seam merge map ---
  // Vertices split at UV seams share the same position but have different
  // indices. Build a canonical map: for each vertex, the lowest-index vertex
  // with the same position. Pass 1 accumulates normals to canonical indices
  // so all seam-split vertices see contributions from ALL adjacent triangles.
  // Pass 2 copies the canonical normal back to each split vertex.
  const canonicalData = new Uint32Array(vertCount);
  {
    // Find vertices that share the same position (UV seam splits) and assign
    // them a common canonical index. Uses union-find so transitive groups
    // (A≈B, B≈C → A,B,C all canonical) are handled correctly.
    const srcPos = posAttr.array as Float32Array;
    // Tight epsilon: only merge true UV seam splits (bit-identical positions).
    // 1e-3 was over-merging vertices on opposite sides of thin features
    // (eyelids, nostrils, lips, ears) — their opposing normals cancelled out.
    const eps = 1e-5;

    // Union-find with path compression
    for (let i = 0; i < vertCount; i++) canonicalData[i] = i;
    const find = (i: number): number => {
      while (canonicalData[i] !== i) {
        canonicalData[i] = canonicalData[canonicalData[i]]; // path compression
        i = canonicalData[i];
      }
      return i;
    };
    const union = (a: number, b: number) => {
      const ra = find(a),
        rb = find(b);
      if (ra !== rb) canonicalData[Math.max(ra, rb)] = Math.min(ra, rb);
    };

    // Sort vertex indices by x-position for efficient neighbor scanning
    const sorted = new Uint32Array(vertCount);
    for (let i = 0; i < vertCount; i++) sorted[i] = i;
    sorted.sort((a, b) => srcPos[a * 3] - srcPos[b * 3]);

    // Scan sorted list — for each vertex, check all following vertices
    // while their x is within epsilon. Union any that match in all 3 axes.
    let _mergeOps = 0;
    for (let i = 0; i < vertCount; i++) {
      const vi = sorted[i];
      const vx = srcPos[vi * 3];
      const vy = srcPos[vi * 3 + 1];
      const vz = srcPos[vi * 3 + 2];

      for (let j = i + 1; j < vertCount; j++) {
        const vj = sorted[j];
        if (srcPos[vj * 3] - vx > eps) break;
        if (Math.abs(srcPos[vj * 3 + 1] - vy) > eps) continue;
        if (Math.abs(srcPos[vj * 3 + 2] - vz) > eps) continue;
        union(vi, vj);
        _mergeOps++;
      }
    }

    // Flatten all roots
    for (let i = 0; i < vertCount; i++) canonicalData[i] = find(i);
  }
  const canonicalSBA = new (StorageBufferAttribute as any)(canonicalData, 1);
  const canonicalBuffer = (storage as any)(canonicalSBA, "uint", vertCount)
    .toReadOnly()
    .setName("canonicalMap");

  // --- Deformation mode uniforms ---
  // TSL's uniform() returns a UniformNode. Updating .value on the returned
  // node propagates to the GPU each frame — no wrapper indirection needed.
  const dqsBlendU = (uniform as any)(0.0);
  const smoothFactorU = (uniform as any)(0.8);

  // Corrective smooth needs adjacency data and a scratch buffer
  let adjOffsetsBuf: any = null;
  let adjListBuf: any = null;
  let smoothScratchBuffer: any = null;
  let smoothScratchSBA: InstanceType<typeof StorageBufferAttribute> | null =
    null;
  let maxNeighbours = 0;

  if (enableDeformModes) {
    const adj = buildAdjacencyForSmooth(indices, canonicalData, vertCount);

    // Compute max neighbour count (needed for fixed-bound Loop in TSL)
    for (let i = 0; i < vertCount; i++) {
      const span = adj.offsets[i + 1] - adj.offsets[i];
      if (span > maxNeighbours) maxNeighbours = span;
    }

    const adjOffsetsSBA = new (StorageBufferAttribute as any)(adj.offsets, 1);
    adjOffsetsBuf = (storage as any)(adjOffsetsSBA, "uint", adj.offsets.length)
      .toReadOnly()
      .setName("adjOffsets");
    const adjListSBA = new (StorageBufferAttribute as any)(adj.adjList, 1);
    adjListBuf = (storage as any)(adjListSBA, "uint", adj.adjList.length)
      .toReadOnly()
      .setName("adjList");

    // Scratch buffer for ping-pong smoothing (vec4 per vertex)
    const scratchData = new Float32Array(vertCount * 4);
    smoothScratchSBA = new (StorageBufferAttribute as any)(scratchData, 4);
    smoothScratchBuffer = (storage as any)(
      smoothScratchSBA,
      "vec4",
      vertCount,
    ).setName("smoothScratch");

    shadingDiag.log(
      "BUFFER",
      "adjacency-alloc",
      `Adjacency for corrective smooth: ${adj.offsets.length} offsets, ${adj.adjList.length} edges, max ${maxNeighbours} neighbours`,
    );
  }

  // Fixed-point scale for int32 atomics (2^16 gives ~0.00002 precision)
  const FIXED_SCALE = 65536.0;
  const fixedScale = float(FIXED_SCALE);
  const invFixedScale = float(1.0 / FIXED_SCALE);
  const morphCountUniform = uint(morphCount);

  // --- Pass 0: Morph blend + sculpt + skin → skinned positions ---
  // Bounds guard: compute dispatches ceil(vertCount/workgroupSize) workgroups,
  // so the last workgroup has extra threads beyond vertCount. Without this
  // check, those threads perform OOB buffer reads/writes and atomic ops
  // that can cause GPU validation errors or device loss on some drivers.
  const vertCountU = uint(vertCount);
  const triCountU = uint(triCount);

  const computePositions = Fn(() => {
    const vi = instanceIndex;

    // Guard: ceil(vertCount/workgroupSize)*workgroupSize > vertCount,
    // so the last workgroup's extra threads must be skipped.
    If(vi.lessThan(vertCountU), () => {
      const basePos = basePosBuffer.element(vi);
      const px = basePos.x.toVar("px");
      const py = basePos.y.toVar("py");
      const pz = basePos.z.toVar("pz");

      // Apply morph targets
      if (morphDeltaBuffer && influenceBuffer) {
        const mi = uint(0).toVar("mi");
        (Loop as any)(morphCountUniform, () => {
          const w = influenceBuffer.element(mi);
          If(abs(w).greaterThan(1e-6), () => {
            const deltaIdx = mi.mul(uint(vertCount)).add(vi);
            const delta = morphDeltaBuffer.element(deltaIdx);
            px.addAssign(delta.x.mul(w));
            py.addAssign(delta.y.mul(w));
            pz.addAssign(delta.z.mul(w));
          });
          mi.addAssign(1);
        });
      }

      // Add sculpt displacement
      const sculpt = sculptBuffer.element(vi);
      px.addAssign(sculpt.x);
      py.addAssign(sculpt.y);
      pz.addAssign(sculpt.z);

      // Bone skinning: bindMatrix → weighted bone blend → bindMatrixInverse
      const pos4 = bindMatUniform.mul(vec4(px, py, pz, 1.0));
      const si = skinIndexBuf.element(vi);
      const sw = skinWeightBuf.element(vi);

      // Standard LBS: weighted sum of bone-transformed positions
      const skinned = boneMatBuf
        .element(si.x)
        .mul(sw.x)
        .mul(pos4)
        .add(boneMatBuf.element(si.y).mul(sw.y).mul(pos4))
        .add(boneMatBuf.element(si.z).mul(sw.z).mul(pos4))
        .add(boneMatBuf.element(si.w).mul(sw.w).mul(pos4));

      const lbsResult = bindMatInvUniform.mul(skinned);

      // DQS approximation: weighted blend of bone matrices, then
      // orthonormalize the upper 3x3 to preserve volume.
      // Uses Gram-Schmidt on columns extracted via mat4 × basis-vec4.
      If(dqsBlendU.greaterThan(0.001), () => {
        // Blend bone matrices (same sum as LBS, but we extract rotation)
        const blendedM = boneMatBuf
          .element(si.x)
          .mul(sw.x)
          .add(boneMatBuf.element(si.y).mul(sw.y))
          .add(boneMatBuf.element(si.z).mul(sw.z))
          .add(boneMatBuf.element(si.w).mul(sw.w));

        // Extract columns via mat4 × basis vectors (TSL-safe column access)
        const col0 = blendedM.mul(vec4(1, 0, 0, 0)); // xyz = column 0 of upper 3x3
        const col1 = blendedM.mul(vec4(0, 1, 0, 0));
        const col3 = blendedM.mul(vec4(0, 0, 0, 1)); // translation

        // Gram-Schmidt orthonormalization on columns 0-2
        // Normalize column 0
        const v0 = vec3(col0.x, col0.y, col0.z);
        const len0 = sqrt(dot(v0, v0));
        const n0 = v0.div(select(len0.greaterThan(1e-8), len0, float(1.0)));

        // Orthogonalize column 1 against column 0, then normalize
        const v1raw = vec3(col1.x, col1.y, col1.z);
        const v1ortho = v1raw.sub(n0.mul(dot(v1raw, n0)));
        const len1 = sqrt(dot(v1ortho, v1ortho));
        const n1 = v1ortho.div(
          select(len1.greaterThan(1e-8), len1, float(1.0)),
        );

        // Column 2 = cross(col0, col1) — guarantees orthonormality
        const n2 = cross(n0, n1);

        // Apply orthonormalized rotation + blended translation to bind-space pos
        const rx = n0.x
          .mul(pos4.x)
          .add(n1.x.mul(pos4.y))
          .add(n2.x.mul(pos4.z))
          .add(col3.x);
        const ry = n0.y
          .mul(pos4.x)
          .add(n1.y.mul(pos4.y))
          .add(n2.y.mul(pos4.z))
          .add(col3.y);
        const rz = n0.z
          .mul(pos4.x)
          .add(n1.z.mul(pos4.y))
          .add(n2.z.mul(pos4.z))
          .add(col3.z);

        const dqsResult = bindMatInvUniform.mul(vec4(rx, ry, rz, 1.0));

        // Blend LBS ↔ DQS
        const blendedPos = mix(
          vec3(lbsResult.x, lbsResult.y, lbsResult.z),
          vec3(dqsResult.x, dqsResult.y, dqsResult.z),
          dqsBlendU,
        );
        positionBuffer
          .element(vi)
          .assign(vec4(blendedPos.x, blendedPos.y, blendedPos.z, 1.0));
      }).Else(() => {
        positionBuffer
          .element(vi)
          .assign(vec4(lbsResult.x, lbsResult.y, lbsResult.z, 1.0));
      });
    });
  })().compute(vertCount);

  // --- Pass 0.5: Clear normal accumulators ---
  // Separated from Pass 0 to stay within WebGPU's 8 storage buffers per
  // shader stage — Pass 0 needs the slot for the interp map buffer.
  const clearAccum = Fn(() => {
    const vi = instanceIndex;
    If(vi.lessThan(vertCountU), () => {
      const vi3 = vi.mul(3);
      atomicStore(accumBuffer.element(vi3), int(0));
      atomicStore(accumBuffer.element(vi3.add(1)), int(0));
      atomicStore(accumBuffer.element(vi3.add(2)), int(0));
    });
  })().compute(vertCount);

  // --- Corrective Smooth pass: Laplacian delta smooth ---
  // Smooths the deformation delta (skinned - base) to reduce LBS artifacts.
  // Uses ping-pong between positionBuffer and smoothScratchBuffer.
  // Only dispatched when enableDeformModes is provided and the flag is enabled.
  // The MannequinRenderer dispatches this N times (from smoothItersU) between
  // Pass 0 and Pass 1.
  let smoothPassA: any = null; // Read from positions, write to scratch
  let smoothPassB: any = null; // Read from scratch, write back to positions

  // Clamp to sane maximum to avoid excessive shader unrolling
  const MAX_ADJ = Math.min(Math.max(maxNeighbours, 1), 40);
  const maxAdjU = uint(MAX_ADJ);

  if (enableDeformModes && adjOffsetsBuf && adjListBuf && smoothScratchBuffer) {
    // Helper: one Laplacian smooth iteration reading `srcBuf`, writing `dstBuf`
    const makeSmoothPass = (srcBuf: any, dstBuf: any) =>
      Fn(() => {
        const vi = instanceIndex;
        If(vi.lessThan(vertCountU), () => {
          const ci = canonicalBuffer.element(vi);
          const start = adjOffsetsBuf.element(ci);
          const end = adjOffsetsBuf.element(ci.add(1));
          const count = end.sub(start).toVar("nCount");

          const cur = srcBuf.element(vi);
          const base = basePosBuffer.element(vi);
          const dx = cur.x.sub(base.x).toVar("dx");
          const dy = cur.y.sub(base.y).toVar("dy");
          const dz = cur.z.sub(base.z).toVar("dz");

          If(count.greaterThan(0), () => {
            const ax = float(0).toVar("ax");
            const ay = float(0).toVar("ay");
            const az = float(0).toVar("az");
            const j = uint(0).toVar("j");
            // Fixed-bound loop with guard (TSL requires compile-time bound)
            (Loop as any)(maxAdjU, () => {
              If(j.lessThan(count), () => {
                const ni = adjListBuf.element(start.add(j));
                const np = srcBuf.element(ni);
                const nb = basePosBuffer.element(ni);
                ax.addAssign(np.x.sub(nb.x));
                ay.addAssign(np.y.sub(nb.y));
                az.addAssign(np.z.sub(nb.z));
              });
              j.addAssign(1);
            });
            const invCount = float(1.0).div(float(count));
            const f = smoothFactorU;
            dx.assign(mix(dx, ax.mul(invCount), f));
            dy.assign(mix(dy, ay.mul(invCount), f));
            dz.assign(mix(dz, az.mul(invCount), f));
          });

          dstBuf
            .element(vi)
            .assign(vec4(base.x.add(dx), base.y.add(dy), base.z.add(dz), 1.0));
        });
      })().compute(vertCount);

    smoothPassA = makeSmoothPass(positionBuffer, smoothScratchBuffer);
    smoothPassB = makeSmoothPass(smoothScratchBuffer, positionBuffer);

    shadingDiag.log(
      "COMPUTE",
      "smooth-loop-bound",
      `Corrective smooth: max ${MAX_ADJ} neighbours per vertex`,
    );
  }

  // --- Pass 1: Per-triangle — accumulate angle-weighted face normals ---
  const computeTriNormals = Fn(() => {
    const ti = instanceIndex;

    If(ti.lessThan(triCountU), () => {
      const i0 = indexBuffer.element(ti.mul(3));
      const i1 = indexBuffer.element(ti.mul(3).add(1));
      const i2 = indexBuffer.element(ti.mul(3).add(2));

      // Read positions from actual vertex indices (skinned geometry)
      const p0v = positionBuffer.element(i0);
      const p1v = positionBuffer.element(i1);
      const p2v = positionBuffer.element(i2);

      // Accumulate normals to canonical indices so seam-split vertices
      // receive contributions from ALL adjacent triangles, not just
      // triangles on their side of the UV seam.
      const c0 = canonicalBuffer.element(i0);
      const c1 = canonicalBuffer.element(i1);
      const c2 = canonicalBuffer.element(i2);

      const a = vec3(p0v.x, p0v.y, p0v.z);
      const b = vec3(p1v.x, p1v.y, p1v.z);
      const c = vec3(p2v.x, p2v.y, p2v.z);

      const ab = b.sub(a);
      const ac = c.sub(a);
      const ba = a.sub(b);
      const bc = c.sub(b);

      // Face normal (unnormalized) — ab × ac matches Three.js CCW winding
      const fn = cross(ab, ac);
      const fnLen = sqrt(dot(fn, fn));

      If(fnLen.greaterThan(1e-12), () => {
        const nn = fn.div(fnLen);

        // Interior angles via atan2(|cross|, dot)
        const angleA = atan(fnLen, dot(ab, ac));
        const angleB = atan(fnLen, dot(ba, bc));
        const angleC = atan(fnLen, dot(ac.negate(), bc.negate()));

        // Atomic accumulate using fixed-point int32 on interleaved scalar buffer
        const accum = (idx: any, weight: any) => {
          const base = idx.mul(3);
          atomicAdd(
            accumBuffer.element(base),
            nn.x.mul(weight).mul(fixedScale).toInt(),
          );
          atomicAdd(
            accumBuffer.element(base.add(1)),
            nn.y.mul(weight).mul(fixedScale).toInt(),
          );
          atomicAdd(
            accumBuffer.element(base.add(2)),
            nn.z.mul(weight).mul(fixedScale).toInt(),
          );
        };

        accum(c0, angleA);
        accum(c1, angleB);
        accum(c2, angleC);
      });
    });
  })().compute(triCount);

  // --- Pass 2: Per-vertex — normalize (read from canonical index) ---
  const normalizeNormals = Fn(() => {
    const vi = instanceIndex;

    If(vi.lessThan(vertCountU), () => {
      // Read accumulated normal from this vertex's canonical representative.
      // Split vertices at UV seams all point to the same canonical index,
      // so they receive the averaged normal from all adjacent triangles.
      const ci = canonicalBuffer.element(vi);
      const ci3 = ci.mul(3);
      const nx = float(atomicLoad(accumBuffer.element(ci3))).mul(invFixedScale);
      const ny = float(atomicLoad(accumBuffer.element(ci3.add(1)))).mul(
        invFixedScale,
      );
      const nz = float(atomicLoad(accumBuffer.element(ci3.add(2)))).mul(
        invFixedScale,
      );

      const len = sqrt(nx.mul(nx).add(ny.mul(ny)).add(nz.mul(nz)));
      const safeLen = select(len.greaterThan(0), len, float(1.0));

      normalBuffer
        .element(vi)
        .assign(vec3(nx.div(safeLen), ny.div(safeLen), nz.div(safeLen)));
    });
  })().compute(vertCount);

  // Log total GPU buffer allocation
  const totalBufferBytes =
    skinnedPosData.byteLength + // positions output
    accumData.byteLength + // normal accumulators
    normalOutData.byteLength + // normals output
    indices.byteLength + // index buffer
    basePosData.byteLength + // base positions
    (morphSBA ? (morphSBA.array as Float32Array).byteLength : 0) + // morph deltas
    influenceData.byteLength + // morph influences
    skinIndexAttr.array.byteLength +
    skinWeightAttr.array.byteLength; // skin data
  shadingDiag.log(
    "BUFFER",
    "gpu-buffer-total",
    `Total GPU buffer allocation: ${(totalBufferBytes / (1024 * 1024)).toFixed(1)} MB for ${vertCount} verts, ${triCount} tris, ${morphCount} morphs`,
  );

  return {
    computeNodes: [
      computePositions,
      clearAccum,
      computeTriNormals,
      normalizeNormals,
    ],
    normalBuffer,
    positionBuffer,
    positionSBA,
    influenceData,
    influenceSBA,
    sculptSBA,
    normalSBA: normalOutSBA,
    smoothPassA,
    smoothPassB,
    dqsBlendUniform: dqsBlendU,
    smoothFactorUniform: smoothFactorU,
    dispose() {
      // Zero out the CPU-side typed arrays so the JS heap no longer holds
      // them. The GPU-side backing for each StorageBufferAttribute is owned
      // by the WebGPURenderer's internal resource cache — it's released
      // when the compute node graph and material that reference these
      // attributes are themselves disposed (which MannequinRenderer does
      // via the cloned scene + owned body material on unmount). Three.js
      // does not expose a direct dispose API on BufferAttribute in this
      // version, so there is no way to force a GPU release earlier here.
      positionSBA.array = new Float32Array(0);
      accumSBA.array = new Int32Array(0);
      normalOutSBA.array = new Float32Array(0);
      indexSBA.array = new Uint32Array(0);
      basePosSBA.array = new Float32Array(0);
      skinIdxSBA.array = new Uint32Array(0);
      skinWgtSBA.array = new Float32Array(0);
      sculptSBA.array = new Float32Array(0);
      canonicalSBA.array = new Uint32Array(0);
      if (morphSBA) {
        morphSBA.array = new Float32Array(0);
      }
      if (influenceSBA) {
        influenceSBA.array = new Float32Array(0);
      }
      if (smoothScratchSBA) {
        smoothScratchSBA.array = new Float32Array(0);
      }
    },
  };
}

/**
 * Copy current morph influences into the GPU pipeline's influence buffer.
 * Returns true if any value changed (caller can skip compute dispatch if false).
 */
export function syncMorphInfluences(
  mesh: THREE.SkinnedMesh,
  influenceData: Float32Array,
): boolean {
  const influences = mesh.morphTargetInfluences;
  if (!influences) return false;
  return syncMorphInfluencesFrom(influences, influenceData);
}

/**
 * Sync morph influences from an arbitrary source array.
 * Used when the mesh's morphTargetInfluences has been temporarily cleared
 * (e.g. render-mode subdivision) and the real influences are stashed.
 */
export function syncMorphInfluencesFrom(
  influences: number[],
  influenceData: Float32Array,
): boolean {
  let changed = false;
  for (let i = 0; i < influences.length && i < influenceData.length; i++) {
    if (influenceData[i] !== influences[i]) {
      influenceData[i] = influences[i];
      changed = true;
    }
  }
  return changed;
}
